import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api, setToken, getToken } from "../lib/api";
import { analytics } from "../lib/analytics";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
      analytics.identify(data.user_id, { email: data.email, plan: data.plan, is_admin: !!data.is_admin });
    } catch (e) {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // CRITICAL: If returning from OAuth callback, skip the /me check.
    // AuthCallback will exchange the session_id and establish the session first.
    if (window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    checkAuth();
  }, [checkAuth]);

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    setToken(data.token);
    setUser(data.user);
    analytics.identify(data.user.user_id, { email: data.user.email, plan: data.user.plan });
    analytics.track("auth.login", { method: "email" });
    return data.user;
  };

  const register = async (email, password, name, opts = {}) => {
    // Capture referral code from localStorage if user came in via affiliate link
    let referred_by_code = null;
    try { referred_by_code = localStorage.getItem("pmpintel_ref_code"); } catch { /* noop */ }
    const { data } = await api.post("/auth/register", {
      email, password, name,
      marketing_opt_in: !!opts.marketing_opt_in,
      consent_accepted_at: new Date().toISOString(),
      referred_by_code: referred_by_code || undefined,
    });
    setToken(data.token);
    setUser(data.user);
    analytics.identify(data.user.user_id, { email: data.user.email, plan: data.user.plan, referred_by: referred_by_code });
    analytics.track("auth.register", { method: "email", referred: !!referred_by_code });
    return data.user;
  };

  const logout = async () => {
    try {
      await api.post("/auth/logout");
    } catch {}
    setToken(null);
    setUser(null);
    analytics.track("auth.logout");
    analytics.reset();
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, register, logout, refresh: checkAuth, setUser }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
